import CheckIcon from "@mui/icons-material/Check";
import { Box, IconButton, Typography } from "@mui/material";
import { BlTooltip } from "Components/_Shared/BlTooltip";
import { Button } from "Components/_Shared/Buttons/Button";
import { HStack } from "Components/_Shared/Core/Stack";
import { ReactComponent as BinIcon } from "Components/_Shared/Icons/Assets/bin.svg";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import type { AgentSummary } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { getAgentFullName } from "./utils";

type Props = {
  readonly advisor: AgentSummary;
  readonly isShared: boolean;
  readonly shareDisabledReason: string | undefined;
  readonly disabled: boolean;
  readonly onShare: () => void;
  readonly onRemove: () => void;
};

export const SharingAdvisorItem = ({ advisor, isShared, shareDisabledReason, disabled, onShare, onRemove }: Props) => {
  const { t } = useResource();
  const fullName = getAgentFullName(advisor);
  const removeLabel = t(Resources.AnalysisDetail.Sharing.Remove);
  const isRemoveDisabled = isShared || disabled;

  return (
    <HStack alignItems="center" justifyContent="space-between" spacing={Spacing.L} py={Spacing.L} px={Spacing.XL}>
      <HStack alignItems="center" spacing={Spacing.L} minWidth={0}>
        <UserInitialsAvatar firstName={advisor.firstName} surname={advisor.lastName} />
        <Box minWidth={0}>
          <Typography fontSize={15} fontWeight={500} noWrap>
            {fullName}
          </Typography>
          <Typography fontSize={13} color="text.secondary">
            {t(Resources.AnalysisDetail.Sharing.AdvisorId, { advisorId: advisor.ssoId })}
          </Typography>
        </Box>
      </HStack>
      <HStack alignItems="center" spacing={Spacing.M} flexShrink={0}>
        {isShared ? (
          <HStack
            alignItems="center"
            spacing={Spacing.S}
            height={32}
            px="14px"
            borderRadius="16px"
            bgcolor="success.light"
            color="success.dark"
          >
            <CheckIcon sx={{ fontSize: 16 }} />
            <Typography fontSize={14} fontWeight={700} color="inherit">
              {t(Resources.AnalysisDetail.Sharing.Shared)}
            </Typography>
          </HStack>
        ) : (
          <Button color="secondary" tooltip={shareDisabledReason} disabled={!!shareDisabledReason || disabled} onClick={onShare}>
            {t(Resources.AnalysisDetail.Sharing.Share)}
          </Button>
        )}
        {/* an empty title keeps MUI from warning about a tooltip on a disabled button */}
        <BlTooltip title={isRemoveDisabled ? "" : removeLabel}>
          <IconButton aria-label={removeLabel} disabled={isRemoveDisabled} onClick={onRemove}>
            <BinIcon />
          </IconButton>
        </BlTooltip>
      </HStack>
    </HStack>
  );
};
